import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Params, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Flow, RoutePaths } from '@global/routePaths';
import { combineLatest, EMPTY, Observable, of } from 'rxjs';
import { environment } from '@environments/environment';
import { Store } from '@ngrx/store';
import { getInitFlow, UpdateInitFlow } from '@store/flow-data/init-flow';
import { catchError, first, map, switchMap, take, tap } from 'rxjs/operators';
import { InitFlowService } from '@core/services/api/http/initFlow.service';
import { getCoupon, ResetCoupon } from '@store/flow-data/coupon';
import { isQuickQuote } from '@global/utils';
import { getReferralCodeUrl, ResetGeneral, UpdateReferralCodeUrl } from '@store/flow-data/general';
import {
  getProjectId,
  getSessionId,
  UpdateDeviceInformation,
  UpdateSessionId,
} from '@store/activity-registry/activity-registry';
import { ActivityRegistryService } from '@core/services/api/http/activityRegistry.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import {
  REFERRAL_CODE_COOPERATION_ID,
  REFERRAL_CODE_ID,
  REFERRAL_CODE_ID_URL,
  REFERRAL_CODE_MARKETING_ID,
} from '@global/constants';
import { Coupon } from '@core/models/swagger';

@Injectable({
  providedIn: 'root',
})
export class NavigationGuard implements CanActivate {
  environment = environment;

  private readonly DESKTOP = 'desktop';
  private readonly TABLET = 'tablet';
  private readonly MOBILE = 'mobile';

  constructor(
    private readonly router: Router,
    private readonly store: Store,
    private readonly initFlowService: InitFlowService,
    private readonly activityRegistryService: ActivityRegistryService,
    private readonly deviceDetectorService: DeviceDetectorService
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    const extraState = this.router.currentNavigation()?.extras?.state;
    const isPre = this.environment.environmentName === 'SND';
    const isDevPreview = !this.environment.production && state.url === '/components-preview';

    // Block by environment
    if (!isPre && !extraState?.allowNavigation) {
      this.redirectToError(route.queryParams, extraState);
      return of(false);
    }

    // Preview or Bypass
    if (isDevPreview || extraState?.skipGuard) {
      return of(true);
    }

    return combineLatest([
      this.store.select(getInitFlow),
      this.store.select(getCoupon),
      this.store.select(getSessionId),
      this.store.select(getReferralCodeUrl),
    ]).pipe(
      first(),
      switchMap(([initFlowData, coupon, sessionId, referralCodeUrl]) => {
        const campaignCode = route.queryParams?.campaignCode;
        const promotionCode = route.queryParams?.promotionCode;
        const referralCode =
          route.queryParams?.referralCode?.slice(0, 20) ?? route.queryParams?.empfehlung?.slice(0, 20);

        if (referralCode !== referralCodeUrl) {
          this.store.dispatch(UpdateReferralCodeUrl({ data: referralCode }));
        }

        const hasValidInitFlow =
          initFlowData &&
          (initFlowData?.campaignInfo?.campaignCode === campaignCode ||
            (!campaignCode && initFlowData?.campaignInfo?.campaignCode === 'defaultCampaignCode')) &&
          initFlowData?.promotionInfo?.promotionCode === promotionCode &&
          sessionId;

        if (hasValidInitFlow) return of(true);

        let queryParamsChanged: boolean = false;

        if (initFlowData?.promotionInfo?.promotionCode !== promotionCode) {
          queryParamsChanged = true;
        }

        return this.handleMissingInitFlow({
          campaignCode,
          promotionCode,
          referralCode,
          coupon,
          route,
          extraState,
          queryParamsChanged,
        });
      })
    );
  }

  private handleMissingInitFlow({
    campaignCode,
    promotionCode,
    referralCode,
    coupon,
    route,
    extraState,
    queryParamsChanged,
  }: {
    campaignCode?: string;
    promotionCode?: string;
    referralCode?: string;
    coupon: Coupon;
    route: ActivatedRouteSnapshot;
    extraState: any;
    queryParamsChanged: boolean;
  }): Observable<boolean> {
    let queryParams = { ...route.queryParams };

    return this.initFlowService.postInitFlow({ campaignCode, promotionCode }).pipe(
      first(),
      tap(initFlowData => {
        const hasReferralCode =
          referralCode ||
          coupon?.externalReferences?.references?.some(ref =>
            [REFERRAL_CODE_ID, REFERRAL_CODE_ID_URL].includes(ref.key)
          );

        if (hasReferralCode) {
          initFlowData.campaignInfo = {
            ...initFlowData.campaignInfo,
            cooperationId: REFERRAL_CODE_COOPERATION_ID,
            marketingId: REFERRAL_CODE_MARKETING_ID,
          };
          queryParams.referralCode = hasReferralCode;
          queryParamsChanged = true;
        }

        if (campaignCode && campaignCode !== initFlowData?.campaignInfo?.campaignCode) {
          queryParams.campaignCode = initFlowData?.campaignInfo?.campaignCode;
          queryParamsChanged = true;
        }

        if (promotionCode && promotionCode !== initFlowData?.promotionInfo?.promotionCode) {
          queryParams.promotionCode = initFlowData?.promotionInfo?.promotionCode;
          queryParamsChanged = true;
        }

        this.store.dispatch(UpdateInitFlow({ data: initFlowData }));
      }),
      switchMap(() =>
        this.store.select(getProjectId).pipe(
          take(1),
          switchMap(projectId =>
            this.activityRegistryService.getInit(projectId).pipe(
              tap(({ sessionId }) => this.store.dispatch(UpdateSessionId({ data: sessionId }))),
              map(() => {
                this.initActivityRegister();

                let routePath: string = RoutePaths.NEW_QUOTATION.VEHICLE_INFORMATION.INSURANCE_SITUATION.route;

                if (isQuickQuote()) {
                  extraState.flow = Flow.QUICK_QUOTE;
                  routePath = RoutePaths.QUICK_QUOTE.QUICK_QUOTE.INSURANCE_HOLDER_DATA.route;
                  this.store.dispatch(ResetCoupon());
                  this.store.dispatch(ResetGeneral());
                  sessionStorage.clear();
                }

                if (!queryParamsChanged && coupon?.situation) {
                  return true;
                }

                this.router.navigate([routePath], {
                  queryParamsHandling: 'merge',
                  queryParams,
                  state: { ...extraState, skipGuard: true },
                });
                return false;
              })
            )
          )
        )
      ),
      catchError(() => {
        this.router.navigate([RoutePaths.GENERIC_ERROR.route]);
        return EMPTY;
      })
    );
  }

  private redirectToError(queryParams: Params, state: any): void {
    this.router.navigate([RoutePaths.GENERIC_ERROR.route], {
      queryParamsHandling: 'merge',
      queryParams,
      state: { ...state, skipGuard: true },
    });
  }

  private initActivityRegister(): void {
    this.store.dispatch(
      UpdateDeviceInformation({
        data: {
          deviceInfo: {
            os: this.deviceDetectorService.os(),
            device: this.deviceDetectorService.device(),
            browser: this.deviceDetectorService.browser(),
            browserVersion: this.deviceDetectorService.browser_version(),
            deviceType: this.getDeviceType(),
            osVersion: this.deviceDetectorService.os_version(),
          },
        },
      })
    );
  }

  private getDeviceType(): string {
    if (this.deviceDetectorService.isDesktop()) {
      return this.DESKTOP;
    }
    if (this.deviceDetectorService.isTablet()) {
      return this.TABLET;
    }
    if (this.deviceDetectorService.isMobile()) {
      return this.MOBILE;
    }
  }
}
